// What a shared competition link actually SAYS.
//
// Three share controls send one — the landing card's icon, the League Details
// header and the in-league "Share league" row — and each used to compose its own
// wording. They are all built here now, so the message the client specified
// cannot drift between them. Client, on the native-sharing task: "please make
// sure that when we set this up, all three share places (landing page, league
// details, and Share league) use the same setup for image and text copy."
//
// THE PREVIEW CARD IS NOT BUILT HERE. It is Open Graph metadata in
// public/index.html, because a messaging app scrapes the HTML without running
// any JavaScript. This file is only the text the sender's own message carries.
//
// Client Rev 4 settled how the two relate, and it is worth stating plainly
// because it looks like a contradiction otherwise:
//
//     "For launch, keep the preview generic. I would not spend a full
//      development day making the preview title dynamic... The native share
//      message should still contain the dynamic league name and, for private
//      leagues, the invite code. That gives the recipient the specific
//      information without requiring server-side rendering. The dynamic message
//      should change based on the competition type, while the preview card
//      remains generic."
//
// So: the CARD is one fixed set of tags for every competition (no SSR needed),
// and the MESSAGE above it carries the name, the code and the prize pool. The
// recipient gets the specific detail; the scraper gets something it can read
// without running our bundle.

// The client's own sentence, used as the og:description in public/index.html.
// Kept here so the two cannot disagree — index.html cannot import it, so the
// comment beside those tags names this constant.
//
// Client Rev 4: "Preview description: Join a trading competition. Trade.
// Compete. Prove it." This REPLACED "Sign up free and compete using live market
// data and virtual cash.", which also used to be pasted into the share message
// itself. It is not any more: every message below is the client's new two- or
// three-line form, with no tagline block.
export const SHARE_TAGLINE =
  "Join a trading competition. Trade. Compete. Prove it.";

// "Preview title: QUSHi League Play" — the same string the OG tags carry, and
// deliberately generic for every competition. Passed as the share title too, so
// a target that shows a title shows the same one the card does.
export const SHARE_PREVIEW_TITLE = "QUSHi League Play";

const fallbackName = "a QUSHi competition";

// The client writes competition names in curly quotes in every one of the three
// messages, and puts the comma INSIDE the closing quote on the private one.
// Copied exactly rather than normalised to straight quotes.
const quoted = (name) =>
  `“${String(name || "").trim() || fallbackName}”`;

/**
 * "$5,000" — the prize pool as the app prints it everywhere else.
 *
 * A mirror of formatPrize in landing/leagueFormat.js. It lives there beside the
 * rest of the landing-page formatting and importing a page module into a util
 * would invert the dependency, so the few lines are repeated rather than the
 * layering broken. If one changes, change both.
 */
const formatPrizePool = (amount) => {
  if (amount === null || amount === undefined || amount === "") return null;
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  const whole = Math.round(n * 100) % 100 === 0;
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  })}`;
};

const isCashGame = (game) =>
  !!game?.is_cash_prize_league || Number(game?.cash_prize_amount) > 0;

const isPrivateLeague = (game) =>
  String(game?.game_type || "").toLowerCase() === "private";

/**
 * The message body, WITHOUT the link.
 *
 * The URL is deliberately not in here. navigator.share({ text, url }) hands the
 * two fields over separately and every target appends the link itself, so
 * putting it in the text as well is how a message ends up with the URL printed
 * twice.
 *
 * Client Rev 4 wrote all three:
 *
 *   Public trading league
 *     Join "{{league_name}}" on QUSHi.
 *
 *   Private league
 *     Join my private league, "{{league_name}}," on QUSHi.
 *     Invite code: {{invite_code}}
 *
 *   Cash game
 *     Join "{{game_name}}" on QUSHi and compete for a {{prize_pool}} prize pool.
 *
 * Note what is NOT here any more: the sign-up tagline that every message used to
 * carry. The client moved that line to the preview card, and a message that
 * repeats the card underneath it reads like a template.
 *
 * Each optional part is dropped rather than printed empty — a private league
 * with no code, or a cash game with no amount set, still shares as a clean
 * sentence.
 */
export const competitionShareText = (game) => {
  const name = game?.title;

  if (isPrivateLeague(game)) {
    const opening = `Join my private league, “${
      String(name || "").trim() || fallbackName
    },” on QUSHi.`;
    const code = String(game?.invite_code || "").trim();
    return code ? `${opening}\n\nInvite code: ${code}` : opening;
  }

  if (isCashGame(game)) {
    const pool = formatPrizePool(game?.cash_prize_amount);
    return pool
      ? `Join ${quoted(name)} on QUSHi and compete for a ${pool} prize pool.`
      : `Join ${quoted(name)} on QUSHi.`;
  }

  return `Join ${quoted(name)} on QUSHi.`;
};

/**
 * The address a shared competition points at, and the ONE place that decides it.
 *
 * `/join/` is what marks a league private and `/leagues/` is the public
 * equivalent — the same two prefixes the create wizard shows the organiser on
 * the "League Created" screen. A cash game is public and shares as one.
 *
 * A private link carries the code as `?c=`, which PrivateLeaguePreview reads to
 * fill the code in for the recipient. That is why the code appears twice in a
 * private share and is not a duplication to tidy away: the link spares a working
 * recipient from typing it, and the visible code is what someone who opens the
 * message on another device can still use by hand.
 */
export const leagueShareUrl = (game, origin = window.location.origin) => {
  const slug = game?.vanity_url;
  if (!slug) return origin;

  if (!isPrivateLeague(game)) return `${origin}/leagues/${slug}`;

  const code = String(game?.invite_code || "").trim();
  return code
    ? `${origin}/join/${slug}?c=${encodeURIComponent(code)}`
    : `${origin}/join/${slug}`;
};

/**
 * THE payload every competition share control passes to shareLink /
 * shareViaFlutter.
 *
 * `text` and `message` are both present on purpose, and they are not redundant:
 *
 *   • `text`    — the body WITHOUT the link, for navigator.share, which adds the
 *                 URL from its own `url` field.
 *   • `message` — the finished string including the link, for the Flutter shell,
 *                 whose native share takes one body. Without it the app would
 *                 have to re-join the parts itself and could get the blank line
 *                 wrong.
 *
 * A Flutter build that predates this simply ignores the extra field, so the web
 * side can ship first.
 */
export const leagueShareData = (game, url) => ({
  url,
  title: SHARE_PREVIEW_TITLE,
  text: competitionShareText(game),
  message: `${competitionShareText(game)}\n\n${url}`,
});
